import type { DifficultyLevel } from "./types";

export interface SpellingWord {
  word: string;
  hint: string;
}

/** Easy words are at most 3 letters. */
export const EASY_MAX_LEN = 3;
/** Medium words are at most 4 letters. */
export const MEDIUM_MAX_LEN = 4;
/** Hard words are at most 6 letters. */
export const HARD_MAX_LEN = 6;

const EASY_WORDS: SpellingWord[] = [
  // Space
  { word: "sun", hint: "☀️ Our home star" },
  { word: "sky", hint: "🌤️ Above the clouds" },
  { word: "orb", hint: "🪐 A round body in space" },
  { word: "ufo", hint: "🛸 Unidentified flying object" },
  { word: "jet", hint: "✈️ A fast flying craft" },
  { word: "pod", hint: "🛸 A small space capsule" },
  { word: "ray", hint: "☀️ A beam of light" },
  // Tech
  { word: "ram", hint: "💾 Fast computer memory" },
  { word: "bit", hint: "0️⃣ Smallest piece of data" },
  { word: "bot", hint: "🤖 Short for robot" },
  { word: "web", hint: "🌐 The internet" },
  { word: "app", hint: "📱 Software on a device" },
  { word: "cpu", hint: "🖥️ The brain of a computer" },
  { word: "led", hint: "💡 A tiny electric light" },
  // Science
  { word: "ion", hint: "⚡ A charged particle" },
  { word: "gas", hint: "💨 Matter that spreads out" },
  { word: "lab", hint: "🔬 Where experiments happen" },
  { word: "dna", hint: "🧬 Life's instruction code" },
  // Engineering
  { word: "nut", hint: "🔩 Twists onto a bolt" },
  { word: "rod", hint: "🔧 A straight metal bar" },
  { word: "pin", hint: "📌 A small holding peg" },
  { word: "cog", hint: "⚙️ A gear tooth wheel" },
  { word: "hub", hint: "🛞 Center of a wheel" },
  // Math
  { word: "add", hint: "➕ Put numbers together" },
  { word: "sum", hint: "➕ The total when you add" },
  { word: "set", hint: "🔢 A math collection" },
  { word: "dot", hint: "· A tiny point" },
  { word: "arc", hint: "📐 Part of a circle" },
  { word: "log", hint: "📈 A growth math record" },
  { word: "hex", hint: "6️⃣ A six-sided shape" },
  { word: "max", hint: "⬆️ The biggest value" },
  { word: "min", hint: "⬇️ The smallest value" },
  { word: "odd", hint: "1️⃣ Not an even number" },
  { word: "avg", hint: "📊 The mean of numbers" },
];

const MEDIUM_WORDS: SpellingWord[] = [
  // Space
  { word: "moon", hint: "🌙 Earth's night light" },
  { word: "mars", hint: "🔴 The red planet" },
  { word: "star", hint: "✨ It twinkles above" },
  { word: "nova", hint: "💥 A bright exploding star" },
  { word: "dust", hint: "🌌 Tiny space particles" },
  { word: "rock", hint: "🪨 A piece of asteroid" },
  { word: "core", hint: "🌍 The center of a planet" },
  { word: "halo", hint: "😇 A glowing ring" },
  { word: "glow", hint: "✨ Soft shining light" },
  { word: "beam", hint: "🔦 A line of light" },
  { word: "void", hint: "🕳️ Empty deep space" },
  { word: "pole", hint: "🧭 Top or bottom of a planet" },
  { word: "spin", hint: "🌀 A planet turning" },
  { word: "dock", hint: "🛸 Where ships connect" },
  { word: "fuel", hint: "⛽ Powers a rocket" },
  { word: "heat", hint: "🔥 Warm energy" },
  { word: "zone", hint: "🗺️ A marked region" },
  { word: "belt", hint: "🪐 Ring of space rocks" },
  { word: "ring", hint: "💍 Saturn wears one" },
  { word: "path", hint: "🛤️ A route through space" },
  { word: "rise", hint: "🌅 Going up at dawn" },
  { word: "tide", hint: "🌊 Pulled by the moon" },
  // Tech
  { word: "code", hint: "💻 Instructions for computers" },
  { word: "data", hint: "📊 Facts and numbers stored" },
  { word: "chip", hint: "🔲 A tiny computer brain" },
  { word: "byte", hint: "8️⃣ Eight bits together" },
  { word: "link", hint: "🔗 Connects two things" },
  { word: "port", hint: "🔌 Where cables plug in" },
  { word: "file", hint: "📁 Saved computer info" },
  { word: "disk", hint: "💿 Stores your data" },
  { word: "scan", hint: "📡 Search for a signal" },
  { word: "grid", hint: "▦ A lined pattern" },
  { word: "cell", hint: "📱 Or a tiny life unit" },
  { word: "mode", hint: "⚙️ A setting or way" },
  { word: "boot", hint: "🚀 Start up a computer" },
  { word: "sync", hint: "🔄 Make things match" },
  { word: "wifi", hint: "📶 Wireless internet" },
  { word: "tech", hint: "🛠️ Short for technology" },
  { word: "loop", hint: "🔁 Code that repeats" },
  { word: "gate", hint: "🚪 Controls a signal on/off" },
  { word: "wire", hint: "🔌 Carries electricity" },
  { word: "node", hint: "🕸️ A point in a network" },
  // Science
  { word: "atom", hint: "⚛️ Tiny building block" },
  { word: "gene", hint: "🧬 A trait instruction" },
  { word: "acid", hint: "🧪 Sour in chemistry" },
  { word: "base", hint: "🧪 Opposite of an acid" },
  { word: "mass", hint: "⚖️ How heavy matter is" },
  { word: "wave", hint: "🌊 Energy moving through space" },
  { word: "lens", hint: "🔍 Bends light in a scope" },
  { word: "melt", hint: "🫠 Ice turning to water" },
  { word: "bond", hint: "🔗 Atoms stuck together" },
  { word: "fuse", hint: "🔥 Join with heat" },
  { word: "burn", hint: "🔥 A chemical reaction" },
  { word: "rust", hint: "🟤 Iron turning orange" },
  { word: "soil", hint: "🌱 Dirt for plants" },
  { word: "neon", hint: "💡 A glowing gas element" },
  { word: "iron", hint: "🔩 A strong metal element" },
  // Engineering
  { word: "gear", hint: "⚙️ Toothed wheel that turns" },
  { word: "bolt", hint: "🔩 A threaded fastener" },
  { word: "weld", hint: "🔥 Join metal with heat" },
  { word: "load", hint: "📦 Weight on a structure" },
  { word: "flow", hint: "💧 Liquid moving steadily" },
  { word: "pump", hint: "⛽ Moves fluid along" },
  { word: "mesh", hint: "🕸️ A woven metal screen" },
  { word: "jack", hint: "🔧 Lifts heavy things up" },
  { word: "pipe", hint: "🚰 A tube for fluids" },
  { word: "tank", hint: "🛢️ Holds fuel or water" },
  { word: "duct", hint: "🌬️ An air passage" },
  { word: "coil", hint: "🌀 Wire wrapped in loops" },
  { word: "axle", hint: "🛞 A rod for wheels" },
  // Math
  { word: "math", hint: "➗ Numbers and shapes" },
  { word: "line", hint: "📏 Straight and endless" },
  { word: "area", hint: "⬜ Space inside a shape" },
  { word: "edge", hint: "📐 Where two faces meet" },
  { word: "face", hint: "🟦 A flat side of a solid" },
  { word: "cube", hint: "🎲 Six equal square sides" },
  { word: "cone", hint: "🍦 A pointy round shape" },
  { word: "plot", hint: "📈 A point on a graph" },
  { word: "mean", hint: "📊 The average of numbers" },
  { word: "root", hint: "√ Opposite of a square" },
  { word: "unit", hint: "1️⃣ One measure step" },
  { word: "pair", hint: "2️⃣ A set of two" },
  { word: "sort", hint: "🔢 Put in order" },
  { word: "zero", hint: "0️⃣ Nothing left" },
  { word: "even", hint: "2️⃣ Divisible by two" },
  { word: "odds", hint: "1️⃣ Not even numbers" },
  { word: "plus", hint: "➕ The add sign" },
  { word: "less", hint: "➖ Smaller amount" },
];

const HARD_WORDS: SpellingWord[] = [
  // Space
  { word: "planet", hint: "🪐 A world in orbit" },
  { word: "rocket", hint: "🚀 Flies to space" },
  { word: "orbit", hint: "🛰️ A path around a body" },
  { word: "comet", hint: "☄️ Icy traveler with a tail" },
  { word: "lunar", hint: "🌙 About the moon" },
  { word: "solar", hint: "☀️ About our sun" },
  { word: "space", hint: "🌌 Where rockets go" },
  { word: "pluto", hint: "❄️ A dwarf planet" },
  { word: "venus", hint: "🟡 A hot neighbor planet" },
  { word: "earth", hint: "🌍 Our home planet" },
  { word: "galaxy", hint: "🌌 Billions of stars together" },
  { word: "nebula", hint: "☁️ A colorful gas cloud" },
  { word: "meteor", hint: "☄️ A shooting space rock" },
  { word: "cosmos", hint: "✨ The whole universe" },
  { word: "launch", hint: "🚀 Blast off from the pad" },
  { word: "thrust", hint: "🔥 Push from an engine" },
  { word: "module", hint: "🛸 A ship section" },
  { word: "vector", hint: "➡️ Direction and size" },
  { word: "apogee", hint: "🔝 Farthest point in orbit" },
  { word: "quasar", hint: "💫 A blazing distant core" },
  { word: "pulsar", hint: "📡 A spinning star beacon" },
  { word: "photon", hint: "💡 A particle of light" },
  { word: "parsec", hint: "📏 A huge space distance" },
  { word: "crater", hint: "🕳️ A bowl on the moon" },
  { word: "zenith", hint: "⬆️ Straight overhead in sky" },
  { word: "phases", hint: "🌓 Moon shape changes" },
  // Tech
  { word: "engine", hint: "🔧 Machine that does work" },
  { word: "sensor", hint: "📡 Detects heat or motion" },
  { word: "robot", hint: "🤖 A machine helper" },
  { word: "system", hint: "🖥️ Parts working together" },
  { word: "coding", hint: "💻 Writing programs" },
  { word: "binary", hint: "0️⃣1️⃣ Base-two numbers" },
  { word: "matrix", hint: "🔢 A grid of numbers" },
  { word: "screen", hint: "🖥️ Shows pictures and text" },
  { word: "signal", hint: "📶 A radio or data wave" },
  { word: "digit", hint: "🔢 A single number 0–9" },
  { word: "switch", hint: "🔛 Turns a circuit on/off" },
  { word: "memory", hint: "💾 Stores programs and data" },
  { word: "server", hint: "🖧 Shares files on a network" },
  { word: "upload", hint: "⬆️ Send data to the cloud" },
  { word: "plugin", hint: "🧩 Extra software add-on" },
  { word: "buffer", hint: "📦 Temporary data holding spot" },
  { word: "laptop", hint: "💻 Portable computer" },
  { word: "tablet", hint: "📱 Flat touch computer" },
  { word: "kernel", hint: "🧠 Core of an operating system" },
  { word: "parser", hint: "🔍 Reads code structure" },
  { word: "script", hint: "📜 A small program file" },
  { word: "cipher", hint: "🔐 Secret code method" },
  // Science
  { word: "energy", hint: "⚡ Power to do work" },
  { word: "proton", hint: "➕ Positive particle in nucleus" },
  { word: "neuron", hint: "🧠 A brain signal cell" },
  { word: "carbon", hint: "🌿 Element in all life" },
  { word: "oxygen", hint: "💨 Gas we breathe" },
  { word: "plasma", hint: "🔥 Super-hot charged gas" },
  { word: "enzyme", hint: "🧪 Speeds up reactions" },
  { word: "fusion", hint: "☀️ Joining atoms for energy" },
  { word: "matter", hint: "🧱 Stuff with mass" },
  { word: "liquid", hint: "💧 Flows but keeps volume" },
  { word: "vapor", hint: "💨 Gas from evaporation" },
  { word: "theory", hint: "💡 A tested science idea" },
  { word: "sample", hint: "🧫 A piece for testing" },
  { word: "magnet", hint: "🧲 Attracts iron" },
  { word: "quartz", hint: "💎 A hard crystal mineral" },
  { word: "sulfur", hint: "🟡 Yellow element, smells sharp" },
  { word: "copper", hint: "🟤 Red metal in wires" },
  { word: "silver", hint: "⚪ Shiny metal conductor" },
  // Engineering
  { word: "design", hint: "📐 Plan before you build" },
  { word: "bridge", hint: "🌉 Spans a gap" },
  { word: "torque", hint: "🔧 Twisting force" },
  { word: "stress", hint: "😰 Force inside a material" },
  { word: "piston", hint: "⬆️ Slides inside a cylinder" },
  { word: "roller", hint: "⚙️ Lets parts spin smoothly" },
  { word: "pulley", hint: "🪢 Wheel that lifts loads" },
  { word: "spring", hint: "🌀 Stores push or pull" },
  { word: "metric", hint: "📏 Meters and grams system" },
  { word: "cement", hint: "🧱 Binds bricks together" },
  { word: "solder", hint: "🔥 Joins wires with metal" },
  { word: "rivet", hint: "🔩 Permanent metal pin" },
  // Math
  { word: "number", hint: "🔢 Used for counting" },
  { word: "square", hint: "⬜ Four equal sides" },
  { word: "circle", hint: "⭕ Round with no corners" },
  { word: "radius", hint: "📏 Center to edge length" },
  { word: "volume", hint: "📦 Space inside a 3D shape" },
  { word: "modulo", hint: "➗ Remainder after divide" },
  { word: "factor", hint: "✖️ Multiplies to make a number" },
  { word: "divide", hint: "➗ Split into equal parts" },
  { word: "vertex", hint: "📐 Corner point of a shape" },
  { word: "scalar", hint: "1️⃣ Just a size, no direction" },
  { word: "linear", hint: "📈 Along a straight line" },
  { word: "cosine", hint: "📐 Trig ratio in a triangle" },
  { word: "secant", hint: "📐 Line that cuts through a curve" },
  { word: "median", hint: "📊 Middle value in a list" },
  { word: "degree", hint: "🌡️ Unit of angle or heat" },
  { word: "series", hint: "➕ Sum of a number pattern" },
  { word: "offset", hint: "↔️ A shift from a start point" },
  { word: "profit", hint: "💰 Gain minus cost" },
  { word: "angle", hint: "📐 Two lines meeting" },
  { word: "prime", hint: "🔢 Divisible only by 1 and itself" },
  { word: "ratio", hint: "⚖️ Compare two amounts" },
  { word: "graph", hint: "📈 Picture of data points" },
];

function assertWordLengths(words: SpellingWord[], maxLen: number, tier: string) {
  for (const { word } of words) {
    if (word.length > maxLen) {
      throw new Error(`Spelling word "${word}" exceeds ${tier} max length ${maxLen}`);
    }
  }
}

assertWordLengths(EASY_WORDS, EASY_MAX_LEN, "easy");
assertWordLengths(MEDIUM_WORDS, MEDIUM_MAX_LEN, "medium");
assertWordLengths(HARD_WORDS, HARD_MAX_LEN, "hard");

export function spellingWordsForDifficulty(level: DifficultyLevel): SpellingWord[] {
  switch (level) {
    case "easy":
      return EASY_WORDS.filter((w) => w.word.length <= EASY_MAX_LEN);
    case "medium":
      return MEDIUM_WORDS.filter((w) => w.word.length <= MEDIUM_MAX_LEN);
    case "hard":
      return HARD_WORDS.filter((w) => w.word.length <= HARD_MAX_LEN);
  }
}

export function pickSpellingWord(level: DifficultyLevel): SpellingWord {
  const pool = spellingWordsForDifficulty(level);
  return pool[Math.floor(Math.random() * pool.length)]!;
}
